/**
 * Parse API failures without surfacing raw HTML from the CRA dev server or proxies.
 */

export function isHtmlString(value) {
  if (typeof value !== "string") return false;
  const t = value.trim().slice(0, 200).toLowerCase();
  return t.startsWith("<!doctype") || t.startsWith("<html") || t.includes("<html");
}

export function isJsonResponse(response) {
  const ct = response?.headers?.get?.("content-type") || "";
  return ct.includes("application/json");
}

/**
 * @param {import('axios').AxiosError | Error} err
 * @param {{ apiBase?: string }} options
 */
export function parseApiError(err, options = {}) {
  const apiBase = options.apiBase || "";

  if (!err) return "Something went wrong. Please try again.";

  const response = err.response;
  const data = response?.data;

  if (typeof data === "string" && isHtmlString(data)) {
    return apiBase
      ? `Received a web page instead of JSON from ${apiBase}. The backend may be offline or the API URL is wrong — run "npm run server" and ensure MongoDB is running.`
      : 'Received a web page instead of JSON. Start the API on port 4000 (npm run server) or fix REACT_APP_DEV_API_URL in .env.development.';
  }

  if (data && typeof data === "object") {
    if (data.message) return String(data.message);
    if (data.error) return String(data.error);
  }

  if (typeof data === "string" && data.trim() && !isHtmlString(data)) {
    return data.trim().slice(0, 300);
  }

  const status = response?.status;
  if (status === 401) {
    return "Invalid email or password.";
  }
  if (status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (status === 404) {
    return "API endpoint not found. Check that the backend is up to date.";
  }
  if (status === 503) {
    return "Service unavailable. MongoDB may not be connected.";
  }
  if (status >= 500) {
    return "Server error. Check the API terminal for details.";
  }

  if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
    return apiBase
      ? `Cannot reach the API at ${apiBase}. Start MongoDB, then run: npm run server`
      : "Cannot reach the API. Start MongoDB, then run: npm run server";
  }

  if (err.message && !isHtmlString(err.message)) {
    return err.message;
  }

  return "Request failed. Please try again.";
}

/**
 * @param {string} healthUrl full URL to GET /api/health
 * @param {{ precheckMessage?: string, mixedContentMessage?: string }} [options]
 */
export async function checkApiHealth(healthUrl, options = {}) {
  if (options.precheckMessage) {
    return { ok: false, message: options.precheckMessage };
  }
  if (options.mixedContentMessage) {
    return { ok: false, message: options.mixedContentMessage };
  }

  try {
    const res = await fetch(healthUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    const ct = res.headers.get("content-type") || "";
    const responseUrl = res.url || healthUrl;
    if (!ct.includes("application/json")) {
      console.error("[API health]", {
        url: responseUrl,
        requested: healthUrl,
        status: res.status,
        contentType: ct || "(empty)",
      });
      const text = await res.text().catch(() => "");
      if (isHtmlString(text) || ct.includes("text/html")) {
        const onLocal =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
        return {
          ok: false,
          message: onLocal
            ? "API health check returned HTML instead of JSON. Run npm run dev (or npm run server), then restart npm start. Or set REACT_APP_DEV_API_URL=http://localhost:4000 in .env.development."
            : "API URL is pointing to frontend, not backend. /api must be proxied to Node.js (see public/.htaccess) or run npm run server:prod with SERVE_CLIENT=1. Test: https://www.itsinception.com/api/health must return JSON.",
        };
      }
      return { ok: false, message: "API health check did not return JSON." };
    }

    const body = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        message: body?.message || `API health check failed (${res.status}).`,
        body,
      };
    }

    if (body?.success === true || body?.status === "ok" || body?.ok === true) {
      return { ok: true, message: body?.message || "", body };
    }

    return {
      ok: false,
      message: body?.message || "API reported unhealthy status.",
      body,
    };
  } catch {
    return {
      ok: false,
      message:
        "Backend server is offline. From the project root run: npm run dev (or npm run server in a second terminal). Ensure MongoDB is running.",
    };
  }
}
